import React from 'react'
import { Helmet } from 'react-helmet'
import { ROUTES } from '../../lib/constants'
import styles from './Construction.module.scss'
import { Image } from '../../components/Image/Image'
import ConstructionMap from '../../images/construction-map.jpg'
import ConstructionMan from '../../images/construction-man.jpg'
import ConstructionPic1 from '../../images/construction-pic1.jpg'
import ConstructionPic2 from '../../images/construction-pic2.jpg'
import ConstructionVidPoster from '../../images/construction-vid-poster.jpg'
import ConstructionVid1 from '../../images/construction-vid1.png'
import ConstructionVid2 from '../../images/construction-vid2.jpg'
import ConstructionRestoration from '../../images/street-restoration.png'
import { Video } from '../../components/Video/Video'

export const Construction = (): React.JSX.Element => {
  return (
    <>
      <Helmet>
        <title>{ROUTES.construction.title}</title>

        <meta property="og:title" content={`${ROUTES.construction.title}`} />
        <meta property="og:url" content="https://www.citysidefiber.com/construction" />
      </Helmet>

      <section className={styles.constructionProcess}>
        <div className="">
          <h2>Our Construction Process</h2>
          <div className={`${styles.stepsWrap} grid-12`}>
            <div className={styles.image}>
              <div className="img rounded">
                <Image
                  sources={[{ src: ConstructionMan, breakpoint: 'default' }]}
                  alt="Cityside construction worker"
                />
              </div>
              <br />
              <div className="img rounded">
                <Image
                  sources={[{ src: ConstructionRestoration, breakpoint: 'default' }]}
                  alt="Cityside restoration"
                />
              </div>
            </div>
            <div className={styles.steps}>
              <div className="step">
                <div className="label">Step #1</div>
                <h3>Design, Engineer and Permit</h3>
                <div className="step-text">
                  The development process kicks off with extensive planning,
                  design, and engineering, with the intention of bringing a
                  fiber network connection past every resident and business
                  within the city. Our team works with City staff to coordinate
                  permitting and a construction schedule that minimizes
                  disruption for residents as much as possible. After permits
                  are received, we will locate public utilities with removable
                  temporary markings in the streets, parkways, and sidewalks.
                </div>
              </div>
              <div className="step">
                <div className="label">Step #2</div>
                <h3>Construction</h3>
                <div className="step-text">
                  The construction process includes the installation of
                  underground conduit, fiber, and access vaults in the public
                  right of way adjacent to the road. Prior to construction, we
                  will deliver multiple notices by mail and in-person with
                  information on the process. When your street is ready to start
                  construction, we will notify residents ahead of time with door
                  hangers, no parking signs, and additional details. Throughout
                  the process, we will provide updates via social media, email,
                  construction signs, and mailers. The construction process is
                  designed to move quickly and safely through each neighborhood,
                  minimizing the impact on residents. Please contact our
                  concierge for a speedy resolution if you have any concerns.
                </div>
              </div>
              <div className="step">
                <div className="label">Step #3</div>
                <h3>Splice, Test, and Connect</h3>
                <div className="step-text">
                  Once the underground conduit and access vaults are in place,
                  our construction teams will install the fiber-optic cables and
                  connect them together to create one seamless network. The
                  network is then tested to ensure that it is up to our quality
                  standards for speed and reliability.
                </div>
              </div>
              <div className="step">
                <div className="label">Step #4</div>
                <h3>Street Restoration</h3>
                <div className="step-text">
                  Final street restoration is determined by each project.
                  Typically, we will install our network underground and fill the micro-trench with concrete slurry on the same day.
                  After 2-4 weeks, we will return to the area to grind out a clean section above the trenched area (about 12”-18” wide along the trench path)
                  and then install new asphalt that is smooth with the existing roadway (see image).
                  Sometimes the city will have plans to return at a later time to coat the street with a slurry seal.
                  In other cases, the city will have plans to install new roadway asphalt, which is why we install our network before that major street construction begins.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.cardsSection}>
        <h2>Connecting Your Home</h2>
        <div className="">
          <div className="cards-wrap">
            <div className="row grid-12">
              <div className="text">
                <div className="label">Step #1</div>
                <h3>Pre-Installation</h3>
                <div className="info">
                  You can now sign up for Cityside Fiber services! Once you’ve
                  set an installation date, our crew will stop by a few days in
                  advance to prepare your home's customized installation. Then
                  they will extend fiber from the right-of-way on your street or
                  sidewalk area (what was laid during construction) up to the
                  side of your home. While other providers will simply leave the
                  wire laying on top of your landscaping for weeks at a time, we
                  bury ours immediately. Doing this protects your internet
                  connection and keeps your yard looking tidy!
                </div>
              </div>
              <div className="img">
                <Image
                  sources={[{ src: ConstructionPic2, breakpoint: 'default' }]}
                  alt="construction cable spool"
                />
              </div>
            </div>
            <div className="row grid-12">
              <div className="text">
                <div className="label">Step #2</div>
                <h3>Installation</h3>
                <div className="info">
                  An installation technician will arrive to activate our
                  fiber-optic network in your home. They do this by connecting
                  fiber directly to a router which provides WiFi to the devices
                  throughout your home. They will test the connection to make
                  sure your speeds are as expected, show you how to connect
                  devices to WiFi, and set up your Cityside MyCityWiFi App so
                  you can manage your services from anywhere!{' '}
                </div>
              </div>
              <div className="img">
                <Image
                  sources={[{ src: ConstructionPic1, breakpoint: 'default' }]}
                  alt="constrution worker holder cable"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className={`${styles.constructionVideosSection}`}>
        <div className="grid-12">
          <h2>Construction Videos</h2>
        </div>
        <div className={`${styles.constructionVideos}`}>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/lsGobRz9DAk?si=0sc2197Ug5YaINwq"
              poster="https://img.youtube.com/vi/lsGobRz9DAk/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/4DD40AG9nWI?si=frPySTF65NLL3_66"
              poster="https://img.youtube.com/vi/4DD40AG9nWI/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/jYSpMAcOMm8?si=gc3W-XjQnIuCwOEV"
              poster="https://img.youtube.com/vi/jYSpMAcOMm8/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
          <div className="video">
            <Video
              src="https://www.youtube.com/embed/his2E4mm8lI?si=eJXIxlX2uwFS_UWp"
              poster="https://img.youtube.com/vi/his2E4mm8lI/maxresdefault.jpg"
              iconColor="white"
            />
          </div>
        </div>
      </section>
    </>
  )
}
